import { IAudition, IProject, IRole, IUserSummary } from '../../types/global/helper';
import { ILike } from '../account/types';

export const types = {
    // Company actions
    GET_COMPANY_DETAILS: 'GET_COMPANY_DETAILS',
    GET_COMPANY_DETAILS_SUCCESS: 'GET_COMPANY_DETAILS_SUCCESS',
    GET_COMPANY_DETAILS_FAIL: 'GET_COMPANY_DETAILS_FAIL',

    CREATE_COMPANY: 'CREATE_COMPANY',
    CREATE_COMPANY_SUCCESS: 'CREATE_COMPANY_SUCCESS',
    CREATE_COMPANY_FAIL: 'CREATE_COMPANY_FAIL',

    UPDATE_COMPANY: 'UPDATE_COMPANY',
    UPDATE_COMPANY_SUCCESS: 'UPDATE_COMPANY_SUCCESS',
    UPDATE_COMPANY_FAIL: 'UPDATE_COMPANY_FAIL',

    DELETE_COMPANY: 'DELETE_COMPANY',
    DELETE_COMPANY_SUCCESS: 'DELETE_COMPANY_SUCCESS',
    DELETE_COMPANY_FAIL: 'DELETE_COMPANY_FAIL',

    // Company search actions
    SEARCH_COMPANIES: 'SEARCH_COMPANIES',
    SEARCH_COMPANIES_SUCCESS: 'SEARCH_COMPANIES_SUCCESS',
    SEARCH_COMPANIES_FAIL: 'SEARCH_COMPANIES_FAIL',

    // Company review actions
    ADD_COMPANY_REVIEW: 'ADD_COMPANY_REVIEW',
    ADD_COMPANY_REVIEW_SUCCESS: 'ADD_COMPANY_REVIEW_SUCCESS',
    ADD_COMPANY_REVIEW_FAIL: 'ADD_COMPANY_REVIEW_FAIL',

    EDIT_COMPANY_REVIEW: 'EDIT_COMPANY_REVIEW',
    EDIT_COMPANY_REVIEW_SUCCESS: 'EDIT_COMPANY_REVIEW_SUCCESS',
    EDIT_COMPANY_REVIEW_FAIL: 'EDIT_COMPANY_REVIEW_FAIL',

    LIKE_COMPANY_REVIEW: 'LIKE_COMPANY_REVIEW',
    LIKE_COMPANY_REVIEW_SUCCESS: 'LIKE_COMPANY_REVIEW_SUCCESS',
    LIKE_COMPANY_REVIEW_FAIL: 'LIKE_COMPANY_REVIEW_FAIL',

    DELETE_COMPANY_REVIEW: 'DELETE_COMPANY_REVIEW',
    DELETE_COMPANY_REVIEW_SUCCESS: 'DELETE_COMPANY_REVIEW_SUCCESS',
    DELETE_COMPANY_REVIEW_FAIL: 'DELETE_COMPANY_REVIEW_FAIL',

    DELETE_COMPANY_REVIEW_LIKE: 'DELETE_COMPANY_REVIEW_LIKE',
    DELETE_COMPANY_REVIEW_LIKE_SUCCESS: 'DELETE_COMPANY_REVIEW_LIKE_SUCCESS',
    DELETE_COMPANY_REVIEW_LIKE_FAIL: 'DELETE_COMPANY_REVIEW_LIKE_FAIL',

    // Position actions
    CREATE_POSITION: 'CREATE_POSITION',
    CREATE_POSITION_SUCCESS: 'CREATE_POSITION_SUCCESS',
    CREATE_POSITION_FAIL: 'CREATE_POSITION_FAIL',

    APPROVE_POSITION: 'APPROVE_POSITION',
    APPROVE_POSITION_SUCCESS: 'APPROVE_POSITION_SUCCESS',
    APPROVE_POSITION_FAIL: 'APPROVE_POSITION_FAIL',

    DELETE_POSITION: 'DELETE_POSITION',
    DELETE_POSITION_SUCCESS: 'DELETE_POSITION_SUCCESS',
    DELETE_POSITION_FAIL: 'DELETE_POSITION_FAIL',
} as const;

export interface ILocation {
    type?: string;
    coordinates?: Array<number>;
    address?: string;
    description?: string;
}

export interface IUser {
    _id: string;
    location: ILocation;
    firstName: string;
    lastName: string;
    profile_picture?: string;
    userName: string;
    verified: boolean;
}

export interface ICompanyReview {
    _id: string;
    review_subject: string;
    review_text: string;
    pros: string;
    cons: string;
    location: ILocation;
    time: string;
    user: IUser;
    rating: number;
    createdAt?: string;
    updatedAt?: string;
    role?: string;
    is_user_disliked?: boolean;
    is_user_liked?: boolean;
    user_dislike: ILike[];
    user_like: ILike[];
    total_likes?: number;
    total_dislikes?: number;
    verified?: boolean;
    edited?: boolean;
    edited_on?: string;
}

export interface ICompanyReviewPagination {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface ICompanyReviews {
    data: ICompanyReview[];
    pagination: ICompanyReviewPagination;
}

export interface IJob {
    _id: string;
    // Add job properties as needed
}

export interface ICampaign {
    _id: string;
    // Add campaign properties as needed
}

export interface IPagination {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface IAuditions {
    data: IRole[];
    pagination: IPagination;
}

export interface IJobs {
    data: IAudition[];
    count: number;
}

export interface ICampaigns {
    data: ICampaign[];
    pagination: IPagination;
}

export interface IPosition {
    _id: string;
    user: IUserSummary;
    title: string;
    created_by: IUserSummary;
    company: ICompany;
    approved: boolean;
    createdAt: string;
    updatedAt: string;
    deleted: boolean;
}

export interface IPositionPagination {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface ISimilarCompany {
    _id: string;
    production_type: string[];
    verified: boolean;
    company_name: string;
    user: IUser;
    profile_picture?: string;
    number_of_reviews: number;
    rating: number;
    number_of_jobs: number;
    slug: string;
}

export interface ILeaderShip {
    data: IPosition[];
    pagination: IPositionPagination;
}

export interface ICompany {
    _id: string;
    user: IUser;
    company_name: string;
    date_of_formation: string;
    min_employees: number;
    max_employees: number;
    min_revennue: number;
    max_revenue: number;
    currency: string;
    location: ILocation;
    company_type: string;
    service: string;
    other_services: string[];
    about: string;
    gallery: string[];
    demo_reel: string[];
    status: string;
    rating: number;
    number_of_reviews: number;
    number_of_jobs: number;
    created_by: IUser;
    verified: boolean;
    email: string;
    phone_number: string;
    avg_salary: string;
    perks: string[];
    values: string[];
    createdAt: string;
    updatedAt: string;
    profile_picture: string;
    social_thumbnail: string;
    slug: string;
    avg_creative_quality: number;
    avg_culture: number;
    avg_payment_experience: number;
    avg_professionalism: number;
    avg_reliability: number;
    avg_satisfaction: number;
    avg_recommend: number;
    leadership?: [
        {
            _id: number;
            position: string;
            user: IUserSummary;
        },
    ];
    short_url?: string;
    company_videos?: IProject[];
}

export interface ICompanyLeadership {
    user: IUserSummary;
    position: string;
}

export interface ICompanyDetails {
    company: ICompany;
    reviews: ICompanyReviews;
    auditions: IAuditions;
    jobs: IAuditions;
    campaigns: ICampaigns;
    leadership?: ILeaderShip;
    otherPositions?: ILeaderShip;
    similarCompanies: ISimilarCompany[];
}

export interface ICompanyResponse {
    status: string;
    data: ICompanyDetails;
}

export interface IGetCompanyDetails {
    slug: string;
    reviewsPage?: number;
    reviewsLimit?: number;
    auditionsPage?: number;
    auditionsLimit?: number;
    jobsPage?: number;
    jobsLimit?: number;
    campaignsPage?: number;
    campaignsLimit?: number;
    withReviews?: boolean;
    withAuditions?: boolean;
    withJobs?: boolean;
    withCampaigns?: boolean;
    withLeadership?: boolean;
    withSimilarCompanies?: boolean;
    singleFetch?: boolean;
    reviewsSortBy?: 'date' | 'rating';
    reviewsSortOrder?: 'desc' | 'asc';
}

export interface ICreateCompany {
    company_name: string;
    location: ILocation;
    profile_picture?: File[];
    demo_reel?: File[];
    gallery?: File[];
    about: string;
    date_of_formation?: string;
    min_employees?: number;
    max_employees?: number;
    min_revennue?: number;
    max_revenue?: number;
    currency?: string;
    company_type?: string;
    production_type?: string[];
    service?: string;
    other_services?: string[];
    email?: string;
    phone_number?: string;
    avg_salary?: string;
    perks?: string[];
    values?: string[];
    leadership?: ICompanyLeadership[];
    singleRawFile?: any;
    rawFiles?: any;
    company_videos?: IProject[];
}

export interface IUpdateCompany {
    id: string;
    body: Partial<ICreateCompany>;
}

export interface IAddCompanyReview {
    company: string;
    review_subject: string;
    pros?: string;
    cons?: string;
    review_text: string;
    rating: number;
    time?: string;
    professionalism: number;
    satisfaction: number;
    creative_quality: number;
    culture: number;
    payment_experience: number;
    reliability: number;
    recommend: number;
}

export interface IEditCompanyReview {
    reviewId: string;
    review_subject: string;
    review_text: string;
    rating: number;
    professionalism?: number;
    satisfaction?: number;
    creative_quality?: number;
    culture?: number;
    payment_experience?: number;
    reliability?: number;
    recommend?: number;
    edited?: boolean;
    edited_on?: string;
}

export interface ILikeCompanyReview {
    reviewId: string;
    like_type: 'like' | 'dislike';
    cache?: string;
    total?: string;
}

export interface IDeleteCompanyReview {
    reviewId: string;
}

export interface ISearchCompanyResult extends Partial<ICompany> {
    _id: string;
    company_name: string;
    date_of_formation: string;
    profile_picture: string;
    slug: string;
}

export interface ISearchCompaniesResponse {
    status: string;
    total_paginated_movie_count: number;
    total_paginated_results: number;
    lastEvaluatedkey: number;
    total_unpaginated_result: number;
    data: Array<{
        results: ISearchCompanyResult[];
        totalCount: Array<{ totalCount: number }>;
    }>;
}

export interface ISearchCompanies {
    searchTerm: string;
}

// Position management interfaces
export interface ICreatePosition {
    company: string;
    user: string;
    title: string;
}

export interface IApprovePosition {
    positionId: string;
}

export interface IDeletePosition {
    positionId: string;
}

export interface ICompanyInitialState {
    companyDetails?: ICompanyDetails;
    gettingCompanyDetails: boolean;
    gettingCompanyDetailsError: any;

    creatingCompany: boolean;
    creatingCompanyError: any;

    updatingCompany: boolean;
    updatingCompanyError: any;

    deletingCompany: boolean;
    deletingCompanyError: any;

    // Search companies state
    searchCompaniesData?: ISearchCompanyResult[];
    searchingCompanies: boolean;
    searchingCompaniesError: any;

    addingReview: boolean;
    addingReviewError: any;

    editingReview: boolean;
    editingReviewError: any;

    likingReview: boolean;
    likingReviewError: any;

    deletingReview: boolean;
    deletingReviewError: any;

    // Position management state
    creatingPosition: boolean;
    creatingPositionError: any;

    approvingPosition: boolean;
    approvingPositionError: any;

    deletingPosition: boolean;
    deletingPositionError: any;
}
